import React, { useEffect, useState } from "react";
import axios from "axios";
import { apiURL } from "../config/config";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import { FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import BackButton from "../components/BackButton";

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const clientsPerPage = 10;
  const navigate = useNavigate();

  const fetchClients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${apiURL}clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("ไม่สามารถโหลดข้อมูลลูกค้าได้ ❌");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const filtered = clients.filter((client) =>
      client.customerName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredClients(filtered);
    setCurrentPage(1);
  }, [searchTerm, clients]);

  const handleDelete = async (client) => {
    const confirm = await Swal.fire({
      title: `แน่ใจหรือไม่ที่จะลบลูกค้า?`,
      text: `${client.customerName.trim()}`,
      icon: "warning",
      background: "#1f2937",
      color: "#fff",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "ใช่, ลบเลย",
      cancelButtonText: "ยกเลิก",
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${apiURL}clients/${client._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("ลบลูกค้าเรียบร้อยแล้ว ✅");
        fetchClients();
      } catch (error) {
        console.error("Error deleting client:", error);
        toast.error("เกิดข้อผิดพลาดในการลบ ❌");
      }
    }
  };

  const handleEdit = (client) => {
    navigate(`/edit-client/${client._id}`);
  };

  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClients.slice(
    indexOfFirstClient,
    indexOfLastClient
  );
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  return (
    <div className="px-4 sm:px-6 py-6 relative min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* ✅ Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <BackButton label="ย้อนกลับ" />
        </div>

        <div>
          <h1 className="text-[26px] font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            Client Management
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            จัดการข้อมูลลูกค้า เพิ่ม แก้ไข หรือลบรายชื่อลูกค้าในระบบ
          </p>
        </div>
      </div>

      {/* ✅ Search & Add Button */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <input
          type="text"
          placeholder="ค้นหาชื่อลูกค้า..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 rounded-md px-4 py-2 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 w-full md:w-1/2 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
        />
        <button
          onClick={() => navigate("/create-client")}
          className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium px-5 py-2.5 rounded-md shadow-sm transition-all duration-200"
        >
          <span className="text-base leading-none">＋</span>
          <span>เพิ่มลูกค้าใหม่</span>
        </button>
      </div>

      {/* ✅ Loader */}
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <ClipLoader size={50} color="#6366F1" />
        </div>
      ) : (
        <>
          {/* ✅ Clients Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {currentClients.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center w-full py-10">
                ไม่พบข้อมูลลูกค้า
              </p>
            ) : (
              currentClients.map((client) => (
                <div
                  key={client._id}
                  className="relative bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-5"
                >
                  {/* Action Buttons */}
                  <div className="absolute top-3 right-3 flex space-x-2">
                    <button
                      onClick={() => handleDelete(client)}
                      className="text-white bg-red-500 hover:bg-red-600 p-2 rounded-full shadow-sm transition"
                      title="Delete"
                    >
                      <FaTrash size={14} />
                    </button>
                    <button
                      onClick={() => handleEdit(client)}
                      className="text-white bg-blue-500 hover:bg-blue-600 p-2 rounded-full shadow-sm transition"
                      title="Edit"
                    >
                      <FaEdit size={14} />
                    </button>
                  </div>

                  {/* Client Info */}
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {client.customerName.trim()}
                  </h2>
                  <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        ชื่อบริษัท:
                      </span>{" "}
                      {client.companyBaseName || "-"}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        เลขผู้เสียภาษี:
                      </span>{" "}
                      {client.taxIdentificationNumber || "-"}
                    </p>
                    <p className="whitespace-pre-line">
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        ที่อยู่:
                      </span>{" "}
                      {client.address || "-"}
                    </p>
                    <p>
                      <span className="font-medium text-gray-600 dark:text-gray-400">
                        เบอร์โทร:
                      </span>{" "}
                      {client.contactPhoneNumber || "-"}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ✅ Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mt-8 gap-4">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClientManagement;
